import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate, useParams } from 'react-router-dom'
import type {
  ClientLibrary,
  ClientLibraryClient,
  ClientLibraryId,
} from 'types/client-library'
import type { Location } from 'react-router-dom'
import type { State } from 'types/redux'
import {
  clientLibraryClientsResetErrors,
  clientLibraryClientUpdate,
} from '@disco/state-management/actions/client-library-client-actions'
import mixpanel from '@disco/api/mixpanel'
import {
  getClientLibraryClient,
  getClientLibraryClients,
} from '@disco/state-management/selectors/client-libraries'
import { getEntityName } from '@disco/utils/entityName'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ErrorText from 'components/ui/error-text'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'
type Props = {
  location: Location
}

const LibrariesLibraryClientsApproveRequest = (
  props: Props,
): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { libraryId, clientId } = useParams<{
    libraryId: string
    clientId: string
  }>()
  const libraryIdNum: ClientLibraryId = parseInt(libraryId!, 10)
  const clientIdNum: number = parseInt(clientId.replace('approve-', '')!, 10)
  const clients: ClientLibrary['clients'] | null | undefined = useSelector(
    (state: State) => getClientLibraryClients(state, libraryIdNum),
  )
  const client: ClientLibraryClient | null | undefined = useSelector(
    (state: State) => getClientLibraryClient(state, libraryIdNum, clientIdNum),
  )
  const [clientWillUpdate, setClientWillUpdate] = useState(false)
  useEffect(() => {
    return () => {
      dispatch(clientLibraryClientsResetErrors(libraryIdNum))
    }
  }, [])
  useEffect(() => {
    if (client?.status && client?.status !== 'approved' && !client?.updating) {
      setClientWillUpdate(true)
      dispatch(
        clientLibraryClientUpdate(libraryIdNum, clientIdNum, {
          status: 'approved',
        }),
      )
      mixpanel.sendPureEvent('Invite accepted')
    }
  }, [client])

  const handleCancel = () => {
    navigate(
      props.location.state?.returnTo ?? `/catalogs/${libraryIdNum}/clients`,
    )
  }

  const renderMessage = (client: ClientLibraryClient | null | undefined) => {
    if (client && clientWillUpdate) {
      return `Granted access to ${getEntityName(client)}`
    } else if (client?.status === 'approved') {
      return `Access already approved for ${getEntityName(client)}`
    } else {
      return 'Client not found in this catalog.'
    }
  }

  return (
    <ModalWindow
      header={<Heading size={700}>Approve request</Heading>}
      footer={
        clients?.errors?.detail && (
          <ErrorText>{clients?.errors?.detail}</ErrorText>
        )
      }
      actions={<Button caption="Close" onClick={handleCancel} />}
      isOpen={true}
      width="medium"
      onClose={handleCancel}
    >
      <DocumentHeadTags title="Approve request | DISCO" />
      {clients?.loading ? <span>Loading...</span> : renderMessage(client)}
    </ModalWindow>
  )
}

export default LibrariesLibraryClientsApproveRequest
